// =============================================================================
// Copyright   : 2020 semantics GmbH
// Date        : $Date: 2026-01-14 17:43:36 +0100 (Wed, 14 Jan 2026) $
// Author      : Dirk Rothe
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 159808 $
// Purpose     : choose table columns
// =============================================================================
// ---Third-party imports
import React from 'react'
import {Icon, Menu, List, Image, Grid, Header, Button} from 'semantic-ui-react'
import clsx from 'clsx'

// ---Framework imports
import log from 'uix/logging'
import {Sel} from 'uix/registries'
import {XTab, HeaderSegment, XMenu, ResizableDialog, DebugFeature} from 'uix/components'
import {keys, mapItems, groupBy, sorted, uniq, dict, size, strip} from 'ldash'
import {useSelector, useActions, useState, useGraphics, useEffect} from 'uix/hooks'
import {checkAcl} from 'uix/auth/acl'

// ---Local imports
import {useTreeFieldSchema} from './hooks'
import {ColumnsById} from './columns'
import {getColumLabel, getColumnNamespaceLabel, useColumnContext} from './columnTypes'
import styles from './styles.scss'
import {VIcon} from 'uix/graphics'

// ---Globals
const GENERAL = '_' // alpha sorted first
// read function names
const cleanColumns = coltype => (coltype.endsWith('Column') ? coltype.slice(0, -6) : coltype)
const coltype = f => {
  let n = ColumnsById[f]?.name
  return cleanColumns(n || '')
}

// XXX: ColumnsById is inplace mutated by column registration
let ColumnsNs = groupBy(keys(ColumnsById), f => (f.includes('.') ? f.split('.')[0] : GENERAL))
//~ log.x(keys(ColumnsById).map(k => [k, coltype(k), ColumnsById[k]?.coltype]))

const sortedCols = (unordered, ordered) => sorted(unordered, colid => ordered.indexOf(colid))

// ---Exports
export const ColumnLister = ({
  selected,
  unchosableColumns,
  chosableColumns,
  onChange,
  datatypes,
  quickUnselect = false,
}) => {
  let unchosableColumnsSet = new Set(unchosableColumns)
  let chosableColumnsSet = new Set(chosableColumns)
  let user = useSelector(Sel.user)

  let [colFilter, setColFilter] = useState('')

  let filterByColFilter = colids => {
    //~ let colFilter = 'metadaten'
    //~ return colids.slice(10, 15)
    colFilter = strip(colFilter).toLowerCase()
    if (colFilter.length > 2) {
      colids = colids.filter(f => {
        let s = translate_field(f, false)
        if (s.includes(f)) return false // untranslated, raw column id
        return s.toLowerCase().includes(colFilter)
      })
    }
    return colids
  }

  let filterBychosable = colids => {
    let res = colids.filter(
      f => !chosableColumns?.length || chosableColumnsSet.has(f) || !f.includes('.')
    )
    res = res.filter(colid => {
      let acl = ColumnsById[colid]?.options?.acl
      return !acl || checkAcl(acl, user)
    })
    return res
  }
  let {columnLabel, columnNamespaceLabel, hiddenColumns} = useColumnContext()
  let schema = useTreeFieldSchema()
  let itypes = useSelector(Sel.itypes)
  let i18n = useSelector(Sel.i18n)
  let [debugOn, setDebugOn] = useState(false)
  let graphics = useGraphics(['types'])

  let [active, setActive] = useState(0)

  let [hover, setHover] = useState(false)
  let [selection, setSelection] = useState({})

  // reset on external selected change
  useEffect(() => {
    setSelection(dict((selected || []).map(e => [e, true])))
  }, [selected])

  let _setSelection = sel => {
    setSelection(sel)
    if (onChange) {
      let colids = mapItems(sel, (colid, selected) => colid, (colid, selected) => selected)
      if (size(chosableColumns) > 0) {
        colids = sortedCols(colids, chosableColumns)
      }
      onChange(colids)
    }
  }
  let toggleSelection = f => _setSelection({...selection, [f]: !selection[f]})
  let clearSelection = () => _setSelection({})

  const mouseEnter = i => {
    setHover(i)
  }
  const mouseLeave = i => {
    setHover(false)
  }

  let translate_field = (f, span) => {
    if (debugOn) return f
    let k = ColumnsById[f]?.options?.i18nKey || `col_${f}`
    return getColumLabel(f, i18n, k, columnLabel, span)
  }
  let translate_ns = (ns, span) =>
    debugOn ? ns : getColumnNamespaceLabel(ns, i18n, columnNamespaceLabel, span)

  let filter_fields = (ns, colids) => {
    if (ColumnsNs[ns]) {
      // merge non basic schema fields from colums spec
      colids = uniq([...colids, ...filterBychosable(ColumnsNs[ns])])
    }
    colids = colids.filter(f => !hiddenColumns.has(f))
    if (!debugOn) {
      colids = colids.filter(f => ColumnsById[f])
    }
    return colids
  }

  let build = datatype_fields => {
    // there are some synthetic cols (options={fields: ....}) that need to be collected
    // by checking if their options.fields intersect with datatype_fields
    let df = new Set(datatype_fields)
    let synth_colids = mapItems(
      ColumnsById,
      colid => colid,
      (colid, {options}) => df.has(options?.fields?.[0])
    )
    // combine all candidates
    let colids = [...synth_colids, ...datatype_fields]

    colids = filterBychosable(colids)

    //XXX: instead of metaparent.* - this includes metaparent.field1, metaparent.field2
    colids = filterByColFilter(colids)
    //~ log.x(colids)

    let by_ns = groupBy(colids, f => (f.includes('.') ? f.split('.')[0] : GENERAL))
    let alpha_sort = sorted(keys(by_ns))
    let ns2fields = alpha_sort.map(ns => [ns, filter_fields(ns, by_ns[ns])])

    const [ICON_CHECKED, ICON_UN_CHECKED, ICON_LOCKED, ICON_INDETERMINATE] = [
      'check square outline',
      'square outline',
      'lock',
      'minus square outline',
    ]
    let get_state_icon = (ns_fields, ignore_unchosable = false) => {
      let _unchosableColumnsSet = ignore_unchosable ? new Set() : unchosableColumnsSet
      if (ns_fields.every(f => _unchosableColumnsSet.has(f) || selection[f])) {
        return ICON_CHECKED
      } else if (ns_fields.every(f => !(_unchosableColumnsSet.has(f) || selection[f]))) {
        return ICON_UN_CHECKED
      }
      return ICON_INDETERMINATE
    }
    let toggleGroupClick = (e, ns, ns_fields) => {
      e.stopPropagation()
      let icon_state = get_state_icon(ns_fields, true)
      let next_selected_state = ![ICON_CHECKED, ICON_INDETERMINATE].includes(icon_state)
      let new_selection = dict(
        ns_fields.filter(f => !unchosableColumnsSet.has(f)).map(f => [f, next_selected_state])
      )
      _setSelection({...selection, ...new_selection})
    }

    let ns2fields_sorted = sorted(ns2fields, {
      key: ([ns, fields]) => translate_ns(ns == GENERAL ? 'general' : ns, false),
      localcmp: true,
    })

    let ns_groups = ns2fields_sorted
      .filter(([ns, ns_fields]) => ns_fields.length)
      .map(([ns, ns_fields], i) => {
        return (
          <XMenu.Item
            onMouseEnter={() => mouseEnter(i)}
            onMouseLeave={() => mouseLeave(i)}
            key={ns}
            onClick={() => setActive(i)}
            active={active === i}
            index={i}
            className={styles.fieldGroupItem}
            title={translate_ns(ns == GENERAL ? 'general' : ns, false)}
          >
            <Icon
              size="large"
              name={get_state_icon(ns_fields)}
              onClick={e => {
                setActive(i)
                toggleGroupClick(e, ns, ns_fields)
              }}
            />
            <span className={styles.title}>
              {translate_ns(ns == GENERAL ? 'general' : ns, false)}
            </span>
            <Icon
              size="large"
              className={clsx(hover === i || active === i ? null : styles.hidden)}
              name={'angle double right'}
              onClick={() => setActive(i)}
            />
          </XMenu.Item>
        )
      })

    let ns_content = ns2fields_sorted
      .filter(([ns, ns_fields]) => ns_fields.length)
      .map(([ns, ns_fields], i) => {
        //~ if (ns == 'metaparent') log.x(ns_fields)
        ns_fields = filterByColFilter(ns_fields)
        //~ if (ns == 'metaparent') log.x(ns_fields)
        return ns_fields.map(f => {
          if (active === i) {
            let is_locked = unchosableColumnsSet.has(f)
            return (
              <XMenu.Item
                active={selection[f]}
                key={f}
                disabled={is_locked}
                onClick={() => !is_locked && toggleSelection(f)}
                className={clsx(is_locked && styles.lockedSelected)}
              >
                <VIcon
                  name={is_locked ? ICON_LOCKED : selection[f] ? ICON_CHECKED : ICON_UN_CHECKED}
                  size="large"
                />
                <List.Header>{translate_field(f)}</List.Header>
                <List.Description>{coltype(f)}</List.Description>
              </XMenu.Item>
            )
          }
        })
      })

    let onArrowClick = (e, direction, i) => {
      e.stopPropagation()
      let s = selected[i]
      let new_selected = [...selected]
      if (direction == 'up') {
        new_selected[i] = new_selected[i - 1]
        new_selected[i - 1] = s
      } else {
        new_selected[i] = new_selected[i + 1]
        new_selected[i + 1] = s
      }

      _setSelection(dict(new_selected.map(s => [s, selection[s]])))
    }
    let selList = selected.map((sel, i) => {
      let header = translate_field(sel)
      if (sel.includes('.')) {
        let ns = sel.split('.')[0]
        header = (
          <>
            {`${translate_ns(ns, false)}: `} {header}
          </>
        )
      }
      return (
        <XMenu.Item className={styles.chosenClmn} key={i}>
          <span className={styles.sortButton}>
            {i > 0 && (
              <Button size="tiny" onClick={e => onArrowClick(e, 'up', i)} icon secondary>
                <VIcon name="chevron up" />
              </Button>
            )}
            {i < selected.length - 1 && (
              <Button size="tiny" onClick={e => onArrowClick(e, 'down', i)} icon secondary>
                <VIcon name="chevron down" />
              </Button>
            )}
          </span>
          <span className={styles.name}>
            <p style={{fontWeight: 'bold', marginBottom: '0'}}>{header}</p>
            <p>{coltype(sel)}</p>
          </span>
          <Button size="small" onClick={() => toggleSelection(sel)} icon secondary>
            <VIcon name="close" />
          </Button>
        </XMenu.Item>
      )
    })

    return (
      <Grid className={styles.grid} style={{height: 'calc(100% - 60px)'}} columns={3}>
        <Grid.Row>
          <Grid.Column>
            <Header as="h2" content="Spaltenkategorien" />
          </Grid.Column>
          <Grid.Column>
            <Header as="h2" content="Spalten auswählen" />
          </Grid.Column>
          <Grid.Column>
            <Header as="h2" content="Ausgewählte Spalten" />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row divided>
          <Grid.Column className={styles.overflow}>
            <XMenu vertical>{ns_groups}</XMenu>
          </Grid.Column>
          <Grid.Column className={styles.overflow}>
            <XMenu vertical>{ns_content}</XMenu>
          </Grid.Column>
          <Grid.Column className={styles.overflow}>
            <XMenu borderless vertical>
              {selList}
            </XMenu>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    )
  }

  let allowed_datatype = new Set((datatypes || []).map(dt => String(dt)))
  let panes = mapItems(
    schema,
    (datatype, fields) => ({
      menuItem: (
        <Menu.Item key={datatype}>
          <span style={{marginLeft: 5}}>
            {datatype != 10 ? (
              <Image inline width="14px" src={graphics.types?.[`${datatype}_0`]} />
            ) : (
              <Icon name="warehouse" />
            )}
          </span>
          <span style={{paddingLeft: 5, paddingRight: 5}}>
            {i18n.get(`types_datatype_${itypes.datatypeMap[datatype]}`)}
          </span>
        </Menu.Item>
      ),
      tabKey: datatype,
      render: () => build(fields),
    }),
    dt => dt != 7 && allowed_datatype.has(dt)
  )

  let selected_menu = quickUnselect
    ? mapItems(
        selection,
        f => (
          <Menu.Item fitted key={f} onClick={() => toggleSelection(f)}>
            <span style={{marginLeft: 3}}>{translate_field(f)}</span>
            <Icon style={{marginTop: 3}} name="close" />
          </Menu.Item>
        ),
        (f, is_selected) => is_selected
      )
    : null
  let menu = (
    <XMenu>
      <XMenu.Input icon="filter" value={colFilter} onValueChange={setColFilter} debounce={1000} />
      {selected_menu}
      <DebugFeature>
        <XMenu.Item active={debugOn} icon="bug" onClick={() => setDebugOn(!debugOn)} />
      </DebugFeature>
      {selected.length > 0 && (
        <XMenu.Item icon="eraser" title="Clear column selection." onClick={clearSelection} />
      )}
    </XMenu>
  )

  return (
    <HeaderSegment
      embedded
      header={i18n.get('col_chooser')}
      // subheader={panes.length == 1 ? panes[0].menuItem : null}
      menu={menu}
      className={styles.columnLister}
      icon="tbl_columns"
    >
      {panes.length == 1 ? (
        panes[0].render()
      ) : (
        <XTab
          menu={{tabular: true}}
          storeKey="ColumnLister"
          style={{height: 'calc(100% - 58px)'}}
          className={styles.tabMenu}
          panes={panes}
        />
      )}
    </HeaderSegment>
  )
}

export const ColumnListerDialog = ({onClose, ...props}) => (
  <ResizableDialog closeOnEsc overflow="hidden" onClose={onClose}>
    <ColumnLister {...props} />
  </ResizableDialog>
)
